/**
 * PreferencesApiClient
 * 
 * Handles user preferences across all domains (auto-deposit, transfer-out, theme, etc.)
 */

import { BaseApiClient } from './BaseApiClient';
import { PreferenceType, PreferenceTypeMap } from '../../types/preferences';
import { NotificationPreferences } from '../../types/notification';

export class PreferencesApiClient extends BaseApiClient {
  /**
   * Get preferences by type (generalized system)
   * @param preferenceType The type of preference to get
   * @returns The preferences of the specified type
   */
  async getPreferences<T extends keyof PreferenceTypeMap>(
    preferenceType: T
  ): Promise<PreferenceType<T>> {
    try {
      return await this.get<PreferenceType<T>>(`/profile/preferences/${preferenceType}`);
    } catch (error: any) {
      // For notification preferences, if user doesn't have preferences set (404),
      // return defaults with all notifications enabled (opt-out model)
      if (preferenceType === 'notification' && (error?.message?.includes('404') || error?.message?.includes('not found'))) {
        console.log('📧 No notification preferences found, returning defaults with all notifications enabled');
        return this.getDefaultNotificationPreferences() as PreferenceType<T>;
      }
      throw error;
    }
  }

  /**
   * Get default notification preferences with all notifications enabled
   * This implements opt-out model: new users get all notifications by default
   */
  private getDefaultNotificationPreferences(): NotificationPreferences {
    return {
      enabled: true,
      emailEnabled: true,
      smsEnabled: true,  // Default to true for new users
      pushEnabled: true,
      events: {
        // All events default to all channels enabled
        tokenCreated: { email: true, sms: false, push: true },
        tokenReceived: { email: true, sms: false, push: true },
        tokenAutoDeposited: { email: true, sms: false, push: true },
        tokenAutoRevealed: { email: true, sms: false, push: true },
        tokenExpiring: { email: true, sms: false, push: true },
        tokenRedeemed: { email: true, sms: false, push: true },
        invoicePaid: { email: true, sms: false, push: true },
        invoiceAutoPaid: { email: true, sms: false, push: true },
        invoiceReceived: { email: true, sms: false, push: true },
        transferCompleted: { email: true, sms: false, push: true },
        transferFailed: { email: true, sms: false, push: true },
        lowBalance: { email: true, sms: false, push: true },
        loginNewDevice: { email: true, sms: true, push: true },
        passwordChanged: { email: true, sms: true, push: true },
        merchantPaymentReceived: { email: false, sms: false, push: true },
        merchantRefundIssued: { email: true, sms: false, push: true },
      }
    };
  }

  /**
   * Update preferences by type (generalized system)
   * @param preferenceType The type of preference to update
   * @param preferences The new preferences
   */
  async updatePreferences<T extends keyof PreferenceTypeMap>(
    preferenceType: T,
    preferences: PreferenceType<T>
  ): Promise<void> {
    await this.post(`/profile/preferences/${preferenceType}`, preferences);
  }

  // All preference methods have been replaced by the generic getPreferences and updatePreferences methods
  // Example usage:
  // Get preferences: await getPreferences<T>(preferenceType)
  // Update preferences: await updatePreferences<T>(preferenceType, preferences)
  //
  // Preference types and their structures:
  // 'transfer-out': {
  //   paypalEmail?: string;
  //   transferOutPreferences?: Record<string, { enabled: boolean }>;
  // }
  // 'auto-deposit': {
  //   autoDepositEnabled: boolean;
  //   autoDepositThreshold?: number;
  // }
  // 'theme': {
  //   themePreferences?: {
  //     selectedTheme?: string;
  //     themeMode?: string;
  //   };
  // }
  // 'autoAccept': {
  //   autoAcceptTokens: boolean;
  // }
  // 'invoicePayment': {
  //   mode: 'auto-pay' | 'always-ask' | 'ask-if-above';
  //   threshold?: number;
  // }
} 